import { html, SP } from '../html.js';
import type { VNode } from '../html.js';

/**
 * A stopped crawl, classified.
 *
 * "Crawl stopped: <error string>" is not a screen, it is a log line with a
 * border. The four ways a crawl actually stops need four different sentences
 * from the operator, and one of them — robots.txt refusing us — is not a
 * failure at all but the politeness rule working, and must not be dressed as a
 * fault the operator should try to route around.
 *
 * Classification reads the message the server sent rather than a code, because
 * the code is not in the SSE contract and the message is.
 */

type Kind = 'robots' | 'login' | 'unreachable' | 'stream' | 'unknown';

function classify(message: string): Kind {
  const m = message.toLowerCase();
  if (m.includes('robots') || m.includes('disallow')) return 'robots';
  if (m.includes('401') || m.includes('403') || m.includes('login') || m.includes('forbidden')) {
    return 'login';
  }
  if (
    m.includes('enotfound') ||
    m.includes('econnrefused') ||
    m.includes('timeout') ||
    m.includes('timed out') ||
    m.includes('dns') ||
    m.includes('certificate')
  ) {
    return 'unreachable';
  }
  if (m.includes('stream')) return 'stream';
  return 'unknown';
}

const HEADLINE: Record<Kind, string> = {
  robots: 'This site asked us not to.',
  login: 'The content is behind a sign-in.',
  unreachable: 'We could not reach the origin.',
  stream: 'The progress stream dropped.',
  unknown: 'The crawl stopped.',
};

export function FailureNotice(props: {
  message: string;
  host: string;
  onRestart: () => void;
}): VNode {
  const kind = classify(props.message);

  const body =
    kind === 'robots'
      ? html`<p class="fatal__p">
            ${props.host} disallows ClawHarborBot/1.0 in its robots.txt, so the crawl stopped at
            intake and no page was fetched. There is no override switch — a crawler that reads
            robots.txt and then ignores it is worse than one that never read it.
          </p>
          <p class="fatal__p fatal__p--muted">
            If ${props.host} is yours, add an allow rule for ClawHarborBot and run this again. If it
            is not, the honest next step is to ask its owner, not to find a way around the file.
          </p>`
      : kind === 'login'
        ? html`<p class="fatal__p">
              The origin answered, but what it served is gated. ClawHarbor never crawls behind a
              login: it holds no credentials, and a manifest built from an authenticated session
              would serve one person's data to everyone who called the tool.
            </p>
            <p class="fatal__p fatal__p--muted">
              A public listing page on the same site — a catalogue, a schedule, a branch finder —
              will usually work where the account area does not.
            </p>`
        : kind === 'unreachable'
          ? html`<p class="fatal__p">
                ${props.host} did not answer. The raw error was:${SP}
                <code class="fatal__code">${props.message}</code>
              </p>
              <p class="fatal__p fatal__p--muted">
                Check the spelling of the host, try it with and without <code>www.</code>, and open
                it in a browser first. Nothing was published and nothing was changed.
              </p>`
          : kind === 'stream'
            ? html`<p class="fatal__p">
                  The crawl may still be running on the server; this page simply lost the
                  connection to it. The raw error was:${SP}
                  <code class="fatal__code">${props.message}</code>
                </p>
                <p class="fatal__p fatal__p--muted">
                  Starting the same URL again is safe — it queues a new job and publishes nothing on
                  its own.
                </p>`
            : html`<p class="fatal__p">
                  <code class="fatal__code">${props.message}</code>
                </p>
                <p class="fatal__p fatal__p--muted">
                  Nothing was published and nothing was changed. The stages that did complete are
                  above, with whatever they found.
                </p>`;

  return html`
    <div class="fatal" role="alert">
      <p class="fatal__h">${HEADLINE[kind]}</p>
      ${body}
      <button type="button" class="btn btn--ghost" onClick=${props.onRestart}>
        Crawl a different URL
      </button>
    </div>
  `;
}
